import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Layer, Map, MapRef, Marker, Popup, Source } from "react-map-gl";
import type { Feature, FeatureCollection, LineString } from "geojson";
import type { LayerProps, Projection } from "react-map-gl";
import configProvider from "config/configProvider";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  BoardHub,
  LayerToggles,
  LiveBoardTrip,
  TripTarget,
  currentPointOf,
  hasBackhaul,
  inConus,
  severityOf,
  targetOf,
  viaBackhaul,
} from "map/liveBoard";
import { BOARD_COLORS, OVERLAY_Z, colorFor } from "./palette";
import { Mark } from "./LegGlyph";
import TripTooltip from "./TripTooltip";
import MapLegend from "./MapLegend";
import { mapUnavailableMessage, useMapWebglRecovery } from "map/useMapWebglRecovery";

/**
 * The map half of the live board.
 *
 * The standalone page drew its own Albers projection into an SVG; here the basemap is Mapbox, the
 * same react-map-gl setup as the vehicles and points maps. What did not change is what is drawn:
 * one mark per trip for where it is, one for where it is heading, and a dashed line between them.
 *
 * Trips are DOM Markers rather than a symbol layer. The board's whole visual language is shape --
 * dot, triangle, square, diamond, plus a dashed severity ring around any two of them -- and a
 * symbol layer would need an icon sprite per shape per colour registered on the style. (The
 * points map documents what borrowing an unregistered sprite looks like: Mapbox warns and draws
 * nothing.) The leg lines do go through a GeoJSON source, because a Marker cannot draw one.
 */

/** Whole-CONUS zoom. Nothing here ever zooms out past it -- the board is a US board. */
const US_VIEW = { longitude: -98.5, latitude: 39.5, zoom: 3.4 };
// No zoom tiers on labels any more. Hub names are a hover (see the hub marker below) because
// forty of them printed at once buried the trips the board exists to show, and the unit/trip
// labels answer only to their own toggle: gating them on zoom as well meant the switch did
// nothing at the zoom the board actually opens at, which reads as broken rather than as a tier.

const LEGS_SOURCE = "live-trip-legs";

/** How long the hover card survives the pointer leaving the mark, so it can be reached. */
const POPUP_GRACE_MS = 260;

/**
 * Layers defined here rather than reused from map/components/layers: those are vehicle layers
 * that hardcode `source: "vehicles"` and a "pin" sprite.
 *
 * Two line layers rather than one because line-dasharray cannot be driven from a feature
 * property, and a returning van's finer dashes are part of how that leg reads.
 */
const legLineLayer: LayerProps = {
  id: "live-leg-line",
  type: "line",
  source: LEGS_SOURCE,
  filter: ["!=", ["get", "leg"], "return"],
  paint: {
    "line-color": ["get", "color"],
    "line-width": 1.6,
    "line-opacity": ["get", "opacity"],
    "line-dasharray": [3, 2.5],
  },
};

const returnLegLineLayer: LayerProps = {
  id: "live-leg-line-return",
  type: "line",
  source: LEGS_SOURCE,
  filter: ["==", ["get", "leg"], "return"],
  paint: {
    "line-color": ["get", "color"],
    "line-width": 1.6,
    "line-opacity": ["get", "opacity"],
    "line-dasharray": [2, 4],
  },
};

const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };

export interface LiveTripsMapProps {
  trips: LiveBoardTrip[];
  hubs: BoardHub[];
  layers: LayerToggles;
  hubFilter: Set<string> | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Hover is shared with the list, so pointing at a row lights up its mark and vice versa. */
  hoveredId: number | null;
  onHover: (id: number | null) => void;
  /** Bumped by the page whenever the map should re-fit to what is on the board. */
  fitToken: number;
  height: number | string;
}

const labelStyle = (color: string): React.CSSProperties => ({
  font: "500 11px/1 'IBM Plex Mono', ui-monospace, Menlo, monospace",
  fill: color,
  paintOrder: "stroke",
  stroke: BOARD_COLORS.ring,
  strokeWidth: 3,
  strokeLinejoin: "round",
});

const LiveTripsMap: FC<LiveTripsMapProps> = ({
  trips,
  hubs,
  layers,
  hubFilter,
  selectedId,
  onSelect,
  hoveredId,
  onHover,
  fitToken,
  height,
}) => {
  const mapRef = useRef<MapRef>(null);
  // Rebuilds the map if its WebGL context dies; see the hook for why.
  const { mapKey, reuseMaps, mapUnavailable, retry } = useMapWebglRecovery(mapRef);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [projection] = useState<Projection>({ name: "mercator" });

  // Collapsing the sidenav widens this container without the window ever resizing, and mapbox only
  // re-measures itself on a window resize -- so the canvas kept the old width and the map sat in a
  // letterbox with dead space beside it. Watch the container instead and tell the map whenever it
  // actually changes size.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => mapRef.current?.resize());
    observer.observe(shell);
    return () => observer.disconnect();
  }, []);

  /**
   * The card outlives the hover by a moment.
   *
   * Leaving a mark fires onHover(null), and the popup hangs 16px off the mark -- so the pointer
   * unmounted the card before it could ever reach it, and the "Open in Flow" link inside was
   * unclickable. Keep drawing the last hovered trip for a grace period, and cancel that period
   * while the pointer is over the card itself. Hover still shows it and moving away still
   * dismisses it; there is just a bridge across the gap now.
   */
  const hoveredTrip = useMemo(() => trips.find((t) => t.tripID === hoveredId) || null, [trips, hoveredId]);
  const [stuckId, setStuckId] = useState<number | null>(null);
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelRelease = useCallback(() => {
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    releaseTimer.current = null;
  }, []);

  const scheduleRelease = useCallback(() => {
    cancelRelease();
    releaseTimer.current = setTimeout(() => setStuckId(null), POPUP_GRACE_MS);
  }, [cancelRelease]);

  useEffect(() => {
    if (hoveredId != null) {
      cancelRelease();
      setStuckId(hoveredId);
    } else {
      scheduleRelease();
    }
  }, [hoveredId, cancelRelease, scheduleRelease]);

  useEffect(() => cancelRelease, [cancelRelease]);

  const hovered = useMemo(
    () => hoveredTrip || trips.find((t) => t.tripID === stuckId) || null,
    [hoveredTrip, trips, stuckId],
  );

  const showUnitLabels = layers.labels;

  /** Every drawable point on the board, for the fit. Out-of-CONUS pairs are already gone. */
  const fitPoints = useMemo(() => {
    const pts: [number, number][] = [];
    trips.forEach((t) => {
      const cur = currentPointOf(t);
      if (cur) pts.push(cur);
      const tgt = targetOf(t);
      if (tgt) pts.push([tgt.lon, tgt.lat]);
    });
    return pts.filter(([lon, lat]) => inConus(lon, lat));
  }, [trips]);

  const fitToTrips = useCallback(() => {
    const map = mapRef.current;
    if (!map || !fitPoints.length) return;
    let [w, s] = fitPoints[0];
    let [e, n] = fitPoints[0];
    fitPoints.forEach(([lon, lat]) => {
      w = Math.min(w, lon);
      e = Math.max(e, lon);
      s = Math.min(s, lat);
      n = Math.max(n, lat);
    });
    // maxZoom keeps a single-trip board from diving to street level; the map's own minZoom is
    // what keeps a coast-to-coast board from zooming out past the country.
    map.fitBounds(
      [
        [w, s],
        [e, n],
      ],
      { padding: 60, maxZoom: 8, duration: 600 }
    );
  }, [fitPoints]);

  // The page bumps fitToken on load and on Show everything.
  useEffect(() => {
    if (fitToken > 0) fitToTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitToken]);

  // Selecting a trip from the list brings the map to it.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || selectedId == null) return;
    const t = trips.find((x) => x.tripID === selectedId);
    if (!t) return;
    const pts: [number, number][] = [];
    const cur = currentPointOf(t);
    if (cur) pts.push(cur);
    const tgt = targetOf(t);
    if (tgt) pts.push([tgt.lon, tgt.lat]);
    const usable = pts.filter(([lon, lat]) => inConus(lon, lat));
    if (!usable.length) return;
    let [w, s] = usable[0];
    let [e, n] = usable[0];
    usable.forEach(([lon, lat]) => {
      w = Math.min(w, lon);
      e = Math.max(e, lon);
      s = Math.min(s, lat);
      n = Math.max(n, lat);
    });
    map.fitBounds(
      [
        [w, s],
        [e, n],
      ],
      { padding: 80, maxZoom: 10, duration: 600 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const legsData = useMemo<FeatureCollection>(() => {
    const features: Feature<LineString>[] = [];
    trips.forEach((t) => {
      // Vehicles-only hides the next stop and the line to it along with the mark.
      if (layers.vehiclesOnly) return;
      const cur = currentPointOf(t);
      const tgt = targetOf(t);
      if (!cur || !tgt) return;
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [cur, [tgt.lon, tgt.lat]] },
        properties: {
          leg: t.leg,
          color: colorFor(t),
          opacity: selectedId != null && selectedId !== t.tripID ? 0.12 : 0.85,
        },
      });
    });
    return features.length ? { type: "FeatureCollection", features } : EMPTY;
  }, [trips, layers.vehiclesOnly, selectedId]);

  /**
   * Selecting a trip dims the others. Backhaul mode does NOT.
   *
   * It used to drop every non-opportunity to 0.12, which inverted what the mode is for: a
   * dispatcher turns it on to assign an ACTIVE DRIVER to a pickup, so the drivers and pickups are
   * exactly what has to stay readable while the opportunities stand out. The purple ring
   * (showBackhaulRing) already marks them, and a ring adds emphasis without taking any away.
   */
  const markerOpacity = (t: LiveBoardTrip) => (selectedId != null && selectedId !== t.tripID ? 0.25 : 1);

  /** Where the hover tooltip hangs: the van if there is one, otherwise the mark that stood in. */
  const hoveredAt = useMemo<[number, number] | null>(() => {
    if (!hovered) return null;
    const cur = currentPointOf(hovered);
    if (cur) return cur;
    const tgt = targetOf(hovered);
    return tgt ? [tgt.lon, tgt.lat] : null;
  }, [hovered]);

  /** A backhaul ring is shown in backhaul mode, and always for trips pulled in by a hub filter. */
  const showBackhaulRing = (t: LiveBoardTrip) =>
    hasBackhaul(t) && (layers.backhaulMode || viaBackhaul(t, hubFilter));

  const haloFor = (t: LiveBoardTrip, kind: "vehicle" | "pickup", isHot: boolean) => {
    if (kind !== "vehicle") return BOARD_COLORS.ring;
    if (isHot) return BOARD_COLORS.accent;
    return layers.backhaulMode && hasBackhaul(t) ? BOARD_COLORS.backhaul : BOARD_COLORS.ring;
  };

  const renderTripMark = (t: LiveBoardTrip, kind: "vehicle" | "pickup", color: string) => {
    const isHot = hovered?.tripID === t.tripID || selectedId === t.tripID;
    const label = kind === "vehicle" ? t.vehicle_unit : `#${t.tripID}`;
    return (
      <svg width={44} height={30} viewBox="-22 -15 44 30" style={{ overflow: "visible", color }}>
        {severityOf(t) !== "ok" ? (
          <circle cx={0} cy={0} r={9} fill="none" stroke={color} strokeWidth={1.5} strokeDasharray="2 2" />
        ) : null}
        {showBackhaulRing(t) ? (
          <circle cx={0} cy={0} r={11} fill="none" stroke={BOARD_COLORS.backhaul} strokeWidth={2.2} />
        ) : null}
        <g style={isHot ? { filter: "drop-shadow(0 0 4px rgba(199,125,0,.9))" } : undefined}>
          {/* The halo is the only thing that changes here: gold when hot, magenta for a backhaul
              opportunity in backhaul mode. It cannot go on a hollow mark, where the same slot is
              the fill -- a gold-filled triangle would read as a fifth status colour -- and it
              never replaces the fill, which is what carries the severity. */}
          <Mark kind={kind} x={0} y={0} r={5.5} ring={haloFor(t, kind, isHot)} />
        </g>
        {(showUnitLabels || isHot) && label ? (
          <text x={9} y={4} style={labelStyle(color)}>
            {label}
          </text>
        ) : null}
      </svg>
    );
  };

  const renderTarget = (t: LiveBoardTrip, tgt: TripTarget, color: string) => {
    const isHot = hovered?.tripID === t.tripID || selectedId === t.tripID;
    const ringed = severityOf(t) !== "ok" && t.leg !== "pending" && currentPointOf(t) != null;
    return (
      <svg width={30} height={30} viewBox="-15 -15 30 30" style={{ overflow: "visible", color }}>
        {ringed ? (
          <circle cx={0} cy={0} r={9} fill="none" stroke={color} strokeWidth={1.5} strokeDasharray="2 2" />
        ) : null}
        <g style={isHot ? { filter: "drop-shadow(0 0 4px rgba(199,125,0,.9))" } : undefined}>
          <Mark kind={tgt.kind} x={0} y={0} r={5} />
        </g>
      </svg>
    );
  };

  return (
    <Box ref={shellRef} sx={{ position: "relative", height, width: "100%" }}>
      <Map
        key={mapKey}
        ref={mapRef}
        mapboxAccessToken={configProvider.MapboxApiKey}
        initialViewState={US_VIEW}
        // Nothing on this board is outside the country, so there is nothing to see further out.
        minZoom={3}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        projection={projection}
        style={{ width: "100%", height: "100%" }}
        // Matching the vehicles and points maps. Without reuseMaps this component mounts a second
        // Mapbox instance under React's development double-mount and the survivor is left with no
        // style at all -- an empty canvas that looks like a bad token.
        reuseMaps={reuseMaps}
        failIfMajorPerformanceCaveat={false}
        onClick={() => {
          if (selectedId != null) onSelect(selectedId); // clicking bare map clears the selection
        }}
      >
        <Source id={LEGS_SOURCE} type="geojson" data={legsData}>
          <Layer {...legLineLayer} />
          <Layer {...returnLegLineLayer} />
        </Source>

        {layers.hubs
          ? hubs.map((h) => (
              <Marker key={`hub-${h.id}`} longitude={h.lon} latitude={h.lat} anchor="center">
                {/* The name is a hover, not a printed label. Every hub shouting its name at once
                    was covering the trips, and a hub's position is the thing being read at a
                    glance -- which of them it is only matters once you go looking. MUI's tooltip
                    rather than the SVG <title> it replaces: <title> waits about a second and
                    renders in OS chrome, which on a dense map reads as nothing happening. */}
                <Tooltip
                  title={`${h.name} hub${h.trips ? ` · ${h.trips} trip${h.trips > 1 ? "s" : ""} on the board` : ""}`}
                  placement="top"
                  arrow
                  enterDelay={0}
                >
                  <svg
                    width={30}
                    height={26}
                    viewBox="-15 -13 30 26"
                    style={{ overflow: "visible", color: BOARD_COLORS.hub, cursor: "default" }}
                  >
                    <g opacity={h.trips ? 1 : 0.55}>
                      <Mark kind="hub" x={0} y={0} r={6} />
                    </g>
                  </svg>
                </Tooltip>
              </Marker>
            ))
          : null}

        {trips.map((t) => {
          const color = colorFor(t);
          const cur = currentPointOf(t);
          const tgt = targetOf(t);
          // A pending trip has no van: it is drawn as its pickup triangle, labelled with the trip
          // number. Vehicles-only therefore drops pending trips entirely -- there is nothing left.
          if (!cur && (layers.vehiclesOnly || !tgt || t.leg !== "pending")) return null;
          const anchor = cur || [tgt.lon, tgt.lat];
          return (
            <Marker
              key={`trip-${t.tripID}`}
              longitude={anchor[0]}
              latitude={anchor[1]}
              anchor="center"
              style={{ opacity: markerOpacity(t), zIndex: selectedId === t.tripID ? 2 : 1 }}
              onClick={() => onSelect(t.tripID)}
            >
              <Box
                sx={{ cursor: "pointer", lineHeight: 0 }}
                onMouseEnter={() => onHover(t.tripID)}
                onMouseLeave={() => onHover(null)}
              >
                {renderTripMark(t, cur ? "vehicle" : "pickup", color)}
              </Box>
            </Marker>
          );
        })}

        {layers.vehiclesOnly
          ? null
          : trips.map((t) => {
              const tgt = targetOf(t);
              // The hub diamond is already on the map, and a pending trip's pickup IS its mark.
              if (!tgt || tgt.kind === "hub" || t.leg === "pending") return null;
              return (
                <Marker
                  key={`tgt-${t.tripID}`}
                  longitude={tgt.lon}
                  latitude={tgt.lat}
                  anchor="center"
                  style={{ opacity: markerOpacity(t) }}
                  onClick={() => onSelect(t.tripID)}
                >
                  <Box
                    sx={{ cursor: "pointer", lineHeight: 0 }}
                    onMouseEnter={() => onHover(t.tripID)}
                    onMouseLeave={() => onHover(null)}
                  >
                    {renderTarget(t, tgt, colorFor(t))}
                  </Box>
                </Marker>
              );
            })}

        {/* The popup sits above every mark. Trip Markers set their own z-index (1, or 2 while
            selected) and a Popup sets none, so without this the marks in the surrounding cluster
            paint straight over the card that is describing one of them -- worst exactly where the
            board is busiest and the hover is most needed. Still below OVERLAY_Z, so the zoom
            buttons and the legend stay clickable on top of it.

            It also carries no fixed anchor. Pinned to "bottom" the card always drew upward, so
            hovering a mark in the top band of the map put its head through the ceiling and the
            first rows -- the ones saying why the mark is red -- were the ones cut off. Left to
            choose, Mapbox flips it below the mark when there is no room above. */}
        {hoveredAt ? (
          <Popup
            longitude={hoveredAt[0]}
            latitude={hoveredAt[1]}
            offset={16}
            closeButton={false}
            closeOnClick={false}
            maxWidth="360px"
            style={{ zIndex: 3 }}
          >
            {/* Entering the card holds it open; leaving it starts the same grace period as
                leaving the mark. Without this the card could be drawn but never used. */}
            <Box onMouseEnter={cancelRelease} onMouseLeave={scheduleRelease}>
              <TripTooltip trip={hovered} />
            </Box>
          </Popup>
        ) : null}
      </Map>

      {/* Only after the hook has tried and failed to rebuild the context MAX_REBUILDS times.
          A lost context normally recovers silently, so this is the rare end state, not the
          first thing a hiccup shows. */}
      {mapUnavailable ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: OVERLAY_Z + 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            bgcolor: "background.paper",
          }}
        >
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={retry}>
                Try again
              </Button>
            }
          >
            {mapUnavailableMessage}
          </Alert>
        </Box>
      ) : null}

      <MapLegend />

      {/* Above the marks. Trip Markers carry an explicit z-index (1, or 2 while selected) and the
          map controls sit at the same corner, so without one of their own they lose the stack and
          trips draw straight over the buttons. */}
      <Box
        sx={{
          position: "absolute",
          right: 8,
          bottom: 8,
          zIndex: OVERLAY_Z,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {[
          { title: "Zoom in", label: <AddIcon fontSize="small" />, act: () => mapRef.current?.zoomIn() },
          { title: "Zoom out", label: <RemoveIcon fontSize="small" />, act: () => mapRef.current?.zoomOut() },
          { title: "Fit to the trips on the board", label: "FIT", act: fitToTrips },
          {
            title: "Show the whole US",
            label: "US",
            act: () => mapRef.current?.flyTo({ center: [US_VIEW.longitude, US_VIEW.latitude], zoom: US_VIEW.zoom }),
          },
        ].map((b) => (
          <Tooltip key={b.title} title={b.title} placement="left">
            <IconButton
              size="small"
              onClick={b.act}
              sx={{
                bgcolor: "background.paper",
                boxShadow: 1,
                borderRadius: 1,
                fontSize: 11,
                fontWeight: 700,
                width: 32,
                height: 32,
                "&:hover": { bgcolor: "background.paper" },
              }}
            >
              {b.label}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

export default LiveTripsMap;
